// ============================================================================
// WorkerEditModal — HR edit of a partner-sourced worker card (2026-06-15).
//
// Edits the worker fields on the service_assignments row: name, phone, training
// status/date, facility access. HR has UPDATE via RLS (hr_update) and bypasses
// the protected-cols guard. Rate / provider / assignment-status changes are
// intentionally NOT here — those route through Sub Replacement / finance flows.
// ============================================================================
import { useState, type FormEvent } from 'react';
import { X as XIcon } from 'lucide-react';
import { updateAssignmentWorker, type PartnerWorkerRow } from './hooks/use-hr-data';
import { rpcErrorMessage } from '../../shared/rpc-error';
import { overlayClickClose } from '../../shared/overlay-close';

function str(v: string | null | undefined): string {
  return v === null || v === undefined ? '' : String(v);
}

interface Props {
  worker: PartnerWorkerRow;
  onClose: () => void;
  onSaved: () => void;
}

export default function WorkerEditModal({ worker, onClose, onSaved }: Props) {
  const [workerName, setWorkerName] = useState(str(worker.worker_name));
  const [workerPhone, setWorkerPhone] = useState(str(worker.worker_phone));
  const [trainingStatus, setTrainingStatus] = useState(str(worker.training_status));
  const [trainingDate, setTrainingDate] = useState(str(worker.training_completion_date));
  const [accessIssued, setAccessIssued] = useState(str(worker.facility_access_issued));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(ev: FormEvent) {
    ev.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await updateAssignmentWorker(worker.assignment_id, {
        worker_name: workerName.trim() || null,
        worker_phone: workerPhone.trim() || null,
        training_status: trainingStatus.trim() || null,
        training_completion_date: trainingDate || null,
        facility_access_issued: accessIssued.trim() || null,
      });
      onSaved();
      onClose();
    } catch (err) {
      console.error('[hr] worker update failed', err);
      setError(rpcErrorMessage(err, 'Could not save worker changes.'));
      setSubmitting(false);
    }
  }

  const company = worker.provider_name ?? worker.provider_id ?? 'Unknown company';

  return (
    <div
      className="fixed inset-0 z-[70] flex items-end justify-center bg-tcs-scrim/60 sm:items-center"
      role="dialog"
      aria-modal="true"
      aria-label="Edit worker"
      {...overlayClickClose(onClose)}
    >
      <form
        onClick={(ev) => ev.stopPropagation()}
        onSubmit={handleSubmit}
        className="max-h-[92vh] w-full max-w-md overflow-y-auto rounded-t-xl bg-tcs-overlay p-5 shadow-xl sm:rounded-xl"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-tcs-text">Edit worker</h2>
            <p className="truncate text-xs text-tcs-text-muted">
              {company} · {worker.facility_name ?? worker.facility_id ?? '—'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-md p-1 text-tcs-text-muted hover:bg-tcs-surface"
          >
            <XIcon className="h-4 w-4" />
          </button>
        </div>

        <div className="mt-4 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Worker name">
              <input value={workerName} onChange={(ev) => setWorkerName(ev.target.value)} className={inputCls} />
            </Field>
            <Field label="Worker phone">
              <input value={workerPhone} onChange={(ev) => setWorkerPhone(ev.target.value)} className={inputCls} />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Training status">
              <input value={trainingStatus} onChange={(ev) => setTrainingStatus(ev.target.value)} className={inputCls} />
            </Field>
            <Field label="Training completed">
              <input type="date" value={trainingDate} onChange={(ev) => setTrainingDate(ev.target.value)} className={inputCls} />
            </Field>
          </div>
          <Field label="Facility access issued">
            <input value={accessIssued} onChange={(ev) => setAccessIssued(ev.target.value)} className={inputCls} />
          </Field>
        </div>

        {error && (
          <p className="mt-3 rounded-md border border-rag-red bg-st-danger-bg p-2 text-xs text-st-danger-fg-strong">{error}</p>
        )}

        <div className="mt-5 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-tcs-border px-3 py-2 text-sm text-tcs-text-secondary hover:bg-tcs-bg-muted"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="rounded-md bg-tcs-inverse px-4 py-2 text-sm font-medium text-tcs-inverse-ink hover:bg-tcs-inverse-hover disabled:opacity-50"
          >
            {submitting ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </form>
    </div>
  );
}

const inputCls =
  'w-full rounded-md border border-tcs-border bg-tcs-field px-3 py-2 text-base focus:border-tcs-border-input focus:outline-none';

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block text-xs">
      <span className="mb-1 block font-medium uppercase tracking-wide text-tcs-text-muted">{label}</span>
      {children}
    </label>
  );
}
